/**
 * The remaining screens: follow-up workspace, organizations, leads, opportunities, pipeline,
 * activities, search, saved views, talent, reference administration, and the module overviews.
 *
 * They share the same components as the primary screens — one DataTable idiom, one triad, one set
 * of badges — so a convention changed in one place changes everywhere (UI Constitution §6).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { tfApi } from '../api/tf_api'
import { useApp } from '../contexts/app_context'
import { PageHeader } from '../components/layout/app_shell'
import { InlineEditCell } from '../components/ui/inline_edit_cell'
import {
  Avatar,
  ConfirmDialog,
  DueBadge,
  EmptyState,
  ErrorState,
  ModalShell,
  Spinner,
  StatusBadge,
  Tag,
  addDaysIso,
  formatShortDate,
} from '../components/ui/primitives'
import { NewOrganizationModal } from '../components/ui/new_person_modal'
import { CompleteFollowUpModal } from './home_daily_workspace'
import type {
  FollowUp,
  Lead,
  Opportunity,
  Organization,
  RelationshipRow,
  SavedView,
  SearchResult,
} from '../types/tf_types'

// --------------------------------------------------------------------------------------------
// Shared loader
// --------------------------------------------------------------------------------------------

function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<{ message: string; technical?: string } | null>(null)

  const run = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await fn())
    } catch (err: any) {
      setError({ message: err?.detail?.message ?? String(err), technical: err?.detail?.technical })
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    void run()
  }, [run])

  return { data, loading, error, reload: run }
}

// --------------------------------------------------------------------------------------------
// Follow-up workspace
// --------------------------------------------------------------------------------------------

const BUCKET_TITLES: Array<[string, string, string]> = [
  ['overdue', 'Overdue', 'error'],
  ['today', 'Today', 'today'],
  ['tomorrow', 'Tomorrow', 'event'],
  ['this_week', 'This Week', 'date_range'],
  ['later', 'Later', 'schedule'],
]

function bucketFor(iso: string): string {
  const target = new Date(`${iso.slice(0, 10)}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const days = Math.round((target.getTime() - today.getTime()) / 86400000)
  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days <= 7) return 'this_week'
  return 'later'
}

export function FollowUpsPage() {
  const { notify, notifyError } = useApp()
  const { data, loading, error, reload } = useLoad(
    () => tfApi.fetchFollowUps({ follow_up_status: 'Pending' }),
    [],
  )
  const [rows, setRows] = useState<RelationshipRow[]>([])
  const [completing, setCompleting] = useState<{ row: RelationshipRow; followUp: FollowUp } | null>(null)

  useEffect(() => {
    void tfApi.fetchRelationships({ page_size: 100 }).then((r) => setRows(r.Items)).catch(() => undefined)
  }, [data])

  const rowFor = (contactId: number) =>
    rows.find((r) => r.ContactId === contactId) ??
    ({ ContactId: contactId, DisplayName: `Contact ${contactId}` } as RelationshipRow)

  const defer = async (f: FollowUp, days: number) => {
    try {
      await tfApi.deferFollowUp(f.FollowUpId, addDaysIso(days))
      notify({ tone: 'ok', title: 'Deferred', body: `Moved to ${formatShortDate(addDaysIso(days))}` })
      await reload()
    } catch (err) {
      notifyError('Could not defer', err)
    }
  }

  const grouped = useMemo(() => {
    const out: Record<string, FollowUp[]> = {}
    for (const f of data?.Items ?? []) {
      const key = bucketFor(f.FollowUpDate)
      ;(out[key] ??= []).push(f)
    }
    return out
  }, [data])

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Engagement / Follow-Ups"
        title="Follow-Ups"
        description="Your work queue, organized by when. Work top to bottom — overdue first. Deferring needs a real date."
        actions={
          <button className="btn secondary" type="button" onClick={() => void reload()}>
            <span className="material-symbols-outlined">refresh</span>Refresh
          </button>
        }
      />
      <div className="body-scroll">
        {error && <ErrorState title="Could not load follow-ups" message={error.message} technical={error.technical} onRetry={reload} />}
        {loading ? <Spinner /> : (data?.Items.length ?? 0) === 0 ? (
          <EmptyState icon="event_available" title="Nothing pending" message="No open follow-ups. Set a next move on a relationship to create one." />
        ) : (
          BUCKET_TITLES.map(([key, title, icon]) => {
            const items = grouped[key] ?? []
            if (!items.length) return null
            return (
              <div className="queue-group" key={key}>
                <div className={`queue-head ${key === 'overdue' ? 'overdue' : key === 'today' ? 'today' : ''}`}>
                  <span className="material-symbols-outlined">{icon}</span>
                  <span className="queue-name">{title}</span>
                  <span className="queue-count">{items.length}</span>
                </div>
                {items.map((f) => {
                  const row = rowFor(f.SubjectId)
                  return (
                    <article className={`wq-item${key === 'overdue' ? ' is-overdue' : key === 'today' ? ' is-today' : ''}`} key={f.FollowUpId}>
                      <div className="wq-main">
                        <div className="wq-title">
                          <Link className="wq-who" to={`/relationships/people/${f.SubjectId}`}>{row.DisplayName}</Link>
                          {row.OrganizationName && <span className="wq-org">· {row.OrganizationName}</span>}
                          {f.FollowUpType && <Tag>{f.FollowUpType}</Tag>}
                          {f.Priority && <Tag>{f.Priority}</Tag>}
                        </div>
                        <div className="wq-move">
                          <span className="material-symbols-outlined">arrow_forward</span>
                          {f.Description || 'No description'}
                        </div>
                        <div className="wq-meta">
                          <DueBadge date={f.FollowUpDate} />
                          {f.OwnerUserId && <span><span className="material-symbols-outlined">person</span>{f.OwnerUserId}</span>}
                        </div>
                      </div>
                      <div className="wq-actions">
                        <button className="btn primary sm" type="button" onClick={() => setCompleting({ row, followUp: f })}>
                          <span className="material-symbols-outlined">check</span>Complete
                        </button>
                        <button className="btn secondary sm" type="button" onClick={() => void defer(f, 7)}>Defer 1w</button>
                      </div>
                    </article>
                  )
                })}
              </div>
            )
          })
        )}
      </div>
      {completing && (
        <CompleteFollowUpModal
          row={completing.row}
          followUp={completing.followUp}
          onClose={() => setCompleting(null)}
          onDone={() => void reload()}
        />
      )}
    </>
  )
}

// --------------------------------------------------------------------------------------------
// Organizations
// --------------------------------------------------------------------------------------------

export function OrganizationsPage() {
  const { notify, notifyError } = useApp()
  const { data, loading, error, reload } = useLoad(() => tfApi.fetchOrganizations(), [])
  const [creating, setCreating] = useState(false)

  const patch = (org: Organization, field: string, label: string) => async (next: string | null) => {
    try {
      await tfApi.updateOrganization(org.ContactCompanyId, { [field]: next })
      notify({ tone: 'ok', title: 'Saved', body: `${label} updated for ${org.CompanyName}` })
      await reload()
    } catch (err) {
      notifyError(`Could not update ${label.toLowerCase()}`, err)
      throw err
    }
  }

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Relationships / Organizations"
        title="Organizations"
        description="Accounts, not isolated contacts. An account with one warm contact is single-threaded — a risk you cannot see from a people list."
        actions={
          <>
            <Link className="btn secondary" to="/data/imports">
              <span className="material-symbols-outlined">upload_file</span>Import
            </Link>
            <button className="btn primary" type="button" onClick={() => setCreating(true)}>
              <span className="material-symbols-outlined">domain_add</span>New Organization
            </button>
          </>
        }
      />
      <div className="body-scroll">
        {error && <ErrorState title="Could not load organizations" message={error.message} technical={error.technical} onRetry={reload} />}
        {loading ? <Spinner /> : (data?.Items.length ?? 0) === 0 ? (
          <EmptyState
            icon="business"
            title="No organizations"
            message="Add one, or import a list."
            action={<button className="btn primary" onClick={() => setCreating(true)}>Add an organization</button>}
          />
        ) : (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="dt">
                <caption className="sr-only">Organizations with industry, contacts, next move and follow-up.</caption>
                <thead>
                  <tr>
                    <th>Organization</th><th>Industry</th><th>Contacts</th>
                    <th>Strategic Value</th><th>Next Move</th><th>Follow-Up</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.Items.map((o) => (
                    <tr key={o.ContactCompanyId}>
                      <td>
                        <div className="person-cell">
                          <span className="avatar"><span className="material-symbols-outlined" style={{ fontSize: 15 }}>business</span></span>
                          <div>
                            <span className="person-name">{o.CompanyName}</span>
                            {o.Website && <div className="person-sub">{o.Website}</div>}
                          </div>
                        </div>
                      </td>
                      <td><span className="person-sub">{o.Industry ?? '—'}</span></td>
                      <td>
                        <b style={{ color: 'var(--tf-brand)' }}>{o.ContactCount}</b>
                        {o.ContactCount === 1 && (
                          <div className="person-sub" title="One contact means the account depends on one person">
                            single-threaded
                          </div>
                        )}
                      </td>
                      <td><span className="person-sub">{o.StrategicValue ?? '—'}</span></td>
                      <td style={{ minWidth: 200 }}>
                        <InlineEditCell
                          value={o.NextMove ?? null}
                          type="longtext"
                          displayClass="next-move"
                          placeholder="Set next move"
                          ariaLabel={`Next move for ${o.CompanyName}`}
                          onCommit={patch(o, 'NextMove', 'Next move')}
                        />
                      </td>
                      <td>
                        <InlineEditCell
                          value={o.NextFollowUpDate?.slice(0, 10) ?? null}
                          type="date"
                          placeholder="Set date"
                          ariaLabel={`Follow-up for ${o.CompanyName}`}
                          onCommit={patch(o, 'NextFollowUpDate', 'Follow-up date')}
                          render={(v) => <DueBadge date={v} />}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-foot"><span>{data!.Items.length} organizations</span></div>
          </div>
        )}
      </div>
      {creating && (
        <NewOrganizationModal onClose={() => setCreating(false)} onCreated={() => void reload()} />
      )}
    </>
  )
}

// --------------------------------------------------------------------------------------------
// Leads
// --------------------------------------------------------------------------------------------

export function LeadsPage() {
  const { options, notify, notifyError } = useApp()
  const { data, loading, error, reload } = useLoad(() => tfApi.fetchLeads(), [])
  const [creating, setCreating] = useState(false)
  const [qualifying, setQualifying] = useState<Lead | null>(null)

  const patch = (lead: Lead, field: string, label: string) => async (next: string | null) => {
    try {
      await tfApi.updateLead(lead.LeadId, { [field]: next })
      notify({ tone: 'ok', title: 'Saved', body: `${label} updated` })
      await reload()
    } catch (err) {
      notifyError(`Could not update ${label.toLowerCase()}`, err)
      throw err
    }
  }

  const qualify = async (lead: Lead) => {
    try {
      const result = await tfApi.qualifyLead(lead.LeadId)
      notify({
        tone: 'ok',
        title: 'Lead qualified',
        body: `Opportunity ${result.OpportunityId} created and linked.`,
      })
      setQualifying(null)
      await reload()
    } catch (err) {
      notifyError('Could not qualify the lead', err)
      setQualifying(null)
    }
  }

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Relationships / Leads"
        title="Leads"
        description="Potential business relationships that still need qualification."
        actions={
          <button className="btn primary" type="button" onClick={() => setCreating(true)}>
            <span className="material-symbols-outlined">add</span>New Lead
          </button>
        }
      />
      <div className="body-scroll">
        {error && <ErrorState title="Could not load leads" message={error.message} technical={error.technical} onRetry={reload} />}
        {loading ? <Spinner /> : (data?.Items.length ?? 0) === 0 ? (
          <EmptyState icon="filter_alt" title="No leads yet" action={<button className="btn primary" onClick={() => setCreating(true)}>Create the first lead</button>} />
        ) : (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="dt">
                <caption className="sr-only">Leads with status, next move, follow-up and value.</caption>
                <thead>
                  <tr><th>Lead</th><th>Status</th><th>Source</th><th>Next Move</th><th>Follow-Up</th><th>Value</th><th /></tr>
                </thead>
                <tbody>
                  {data!.Items.map((l) => (
                    <tr key={l.LeadId}>
                      <td><span className="person-name">{l.LeadName}</span></td>
                      <td>
                        <InlineEditCell
                          value={l.LeadStatus}
                          type="select"
                          options={options('LEAD_STATUS').map((v) => ({ value: v.ValueLabel, label: v.ValueLabel }))}
                          placeholder="Set status"
                          ariaLabel={`Status for ${l.LeadName}`}
                          onCommit={patch(l, 'LeadStatus', 'Lead status')}
                          render={(v) => <StatusBadge domain="LEAD_STATUS" value={v} />}
                        />
                      </td>
                      <td><span className="person-sub">{l.LeadSource ?? '—'}</span></td>
                      <td style={{ minWidth: 200 }}>
                        <InlineEditCell
                          value={l.NextMove}
                          type="longtext"
                          displayClass="next-move"
                          placeholder="Set next move"
                          ariaLabel={`Next move for ${l.LeadName}`}
                          onCommit={patch(l, 'NextMove', 'Next move')}
                        />
                      </td>
                      <td>
                        <InlineEditCell
                          value={l.NextFollowUpDate?.slice(0, 10) ?? null}
                          type="date"
                          placeholder="Set date"
                          ariaLabel={`Follow-up for ${l.LeadName}`}
                          onCommit={patch(l, 'NextFollowUpDate', 'Follow-up date')}
                          render={(v) => <DueBadge date={v} />}
                        />
                      </td>
                      <td className="num">{l.EstimatedValue ? `$${Number(l.EstimatedValue).toLocaleString()}` : '—'}</td>
                      <td>
                        {l.ConvertedOpportunityId ? (
                          <span className="person-sub">Converted</span>
                        ) : (
                          <button className="btn secondary sm" type="button" onClick={() => setQualifying(l)}>
                            <span className="material-symbols-outlined">trending_up</span>Qualify
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-foot"><span>{data!.Items.length} leads</span></div>
          </div>
        )}
      </div>

      {creating && <CreateLeadModal onClose={() => setCreating(false)} onDone={() => void reload()} />}
      {qualifying && (
        <ConfirmDialog
          title="Qualify this lead?"
          message={`This creates an opportunity from “${qualifying.LeadName}”, carries the contact across, and links the two. The lead moves to Opportunity Identified.`}
          confirmLabel="Qualify"
          onConfirm={() => void qualify(qualifying)}
          onCancel={() => setQualifying(null)}
        />
      )}
    </>
  )
}

function CreateLeadModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { options, notify, notifyError } = useApp()
  const [name, setName] = useState('')
  const [status, setStatus] = useState('New')
  const [source, setSource] = useState('Referral')
  const [need, setNeed] = useState('')
  const [nextMove, setNextMove] = useState('')
  const [date, setDate] = useState(addDaysIso(7))
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await tfApi.createLead({
        LeadName: name, LeadStatus: status, LeadSource: source,
        BusinessNeed: need || undefined, NextMove: nextMove || undefined,
        NextFollowUpDate: date || undefined,
      })
      notify({ tone: 'ok', title: 'Lead created', body: name })
      onDone(); onClose()
    } catch (err) {
      notifyError('Could not create the lead', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell
      title="New lead"
      description="A lead is a potential business relationship that still needs qualification."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="button" disabled={busy || !name} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Create'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="form-label" htmlFor="cl-name">Lead name</label>
          <input id="cl-name" className="form-input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="cl-status">Status</label>
          <select id="cl-status" className="form-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {options('LEAD_STATUS').map((v) => <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="cl-source">Source</label>
          <select id="cl-source" className="form-input" value={source} onChange={(e) => setSource(e.target.value)}>
            {options('SOURCE').map((v) => <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>)}
          </select>
        </div>
        <div className="field span-2">
          <label className="form-label" htmlFor="cl-need">Business need</label>
          <textarea id="cl-need" className="form-input" rows={2} value={need} onChange={(e) => setNeed(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="cl-move">Next move</label>
          <input id="cl-move" className="form-input" value={nextMove} onChange={(e) => setNextMove(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="cl-date">Follow-up</label>
          <input id="cl-date" className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>
    </ModalShell>
  )
}

// --------------------------------------------------------------------------------------------
// Opportunities and pipeline
// --------------------------------------------------------------------------------------------

/**
 * Stages come from the OPPORTUNITY_STAGE reference domain, never from a constant here. Adding a
 * stage is an Administration change; a hardcoded list would make it a release (Constitution 4.4).
 */
function useStages(): string[] {
  const { options } = useApp()
  return useMemo(() => options('OPPORTUNITY_STAGE').map((v) => v.ValueLabel), [options])
}

export function OpportunitiesPage() {
  const { notify, notifyError } = useApp()
  const stages = useStages()
  const { data, loading, error, reload } = useLoad(() => tfApi.fetchOpportunities(), [])

  const patch = (o: Opportunity, field: string, label: string) => async (next: string | null) => {
    try {
      await tfApi.updateOpportunity(o.OpportunityId, { [field]: next })
      notify({ tone: 'ok', title: 'Saved', body: `${label} updated` })
      await reload()
    } catch (err) {
      notifyError(`Could not update ${label.toLowerCase()}`, err)
      throw err
    }
  }

  const total = (data?.Items ?? []).reduce((sum, o) => sum + Number(o.EstimatedValue ?? 0), 0)

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Relationships / Opportunities"
        title="Opportunities"
        description={`${data?.Items.length ?? 0} opportunities · $${total.toLocaleString()} estimated. Every row shows its next action — a stage with no next action is a stalled deal.`}
        actions={<Link className="btn secondary" to="/relationships/pipeline"><span className="material-symbols-outlined">view_kanban</span>Kanban</Link>}
      />
      <div className="body-scroll">
        {error && <ErrorState title="Could not load opportunities" message={error.message} technical={error.technical} onRetry={reload} />}
        {loading ? <Spinner /> : (data?.Items.length ?? 0) === 0 ? (
          <EmptyState icon="trending_up" title="No opportunities" message="Qualify a lead to create one." />
        ) : (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="dt">
                <caption className="sr-only">Opportunities with stage, value, probability, close date and next action.</caption>
                <thead>
                  <tr><th>Opportunity</th><th>Type</th><th>Stage</th><th>Value</th><th>Probability</th><th>Next Action</th><th>Follow-Up</th></tr>
                </thead>
                <tbody>
                  {data!.Items.map((o) => (
                    <tr key={o.OpportunityId}>
                      <td><span className="person-name">{o.OpportunityName}</span></td>
                      <td>{o.OpportunityType ? <Tag>{o.OpportunityType}</Tag> : '—'}</td>
                      <td>
                        <InlineEditCell
                          value={o.Stage}
                          type="select"
                          options={stages.map((s) => ({ value: s, label: s }))}
                          placeholder="Set stage"
                          ariaLabel={`Stage for ${o.OpportunityName}`}
                          onCommit={patch(o, 'Stage', 'Stage')}
                          render={(v) => <StatusBadge domain="OPPORTUNITY_STAGE" value={v} />}
                        />
                      </td>
                      <td className="num"><b style={{ color: 'var(--tf-brand)' }}>{o.EstimatedValue ? `$${Number(o.EstimatedValue).toLocaleString()}` : '—'}</b></td>
                      <td className="num"><span className="person-sub">{o.Probability ?? 0}%</span></td>
                      <td style={{ minWidth: 200 }}>
                        <InlineEditCell
                          value={o.NextAction}
                          type="longtext"
                          displayClass="next-move"
                          placeholder="Set next action"
                          ariaLabel={`Next action for ${o.OpportunityName}`}
                          onCommit={patch(o, 'NextAction', 'Next action')}
                        />
                      </td>
                      <td>
                        <InlineEditCell
                          value={o.NextFollowUpDate?.slice(0, 10) ?? null}
                          type="date"
                          placeholder="Set date"
                          ariaLabel={`Follow-up for ${o.OpportunityName}`}
                          onCommit={patch(o, 'NextFollowUpDate', 'Follow-up date')}
                          render={(v) => <DueBadge date={v} />}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-foot"><span>{data!.Items.length} opportunities · ${total.toLocaleString()}</span></div>
          </div>
        )}
      </div>
    </>
  )
}

export function PipelinePage() {
  const { notify, notifyError } = useApp()
  const stages = useStages()
  const { data, loading, error, reload } = useLoad(() => tfApi.fetchOpportunities(), [])

  const move = async (o: Opportunity, stage: string) => {
    try {
      await tfApi.updateOpportunity(o.OpportunityId, { Stage: stage })
      notify({ tone: 'ok', title: 'Stage changed', body: `${o.OpportunityName} → ${stage}` })
      await reload()
    } catch (err) {
      notifyError('Could not change the stage', err)
    }
  }

  const byStage = useMemo(() => {
    const out: Record<string, Opportunity[]> = {}
    for (const s of stages) out[s] = []
    //  An opportunity whose stage is not in the reference list still gets a column rather than
    //  vanishing — a record you cannot see is a record you cannot fix.
    for (const o of data?.Items ?? []) (out[o.Stage ?? 'Unassigned'] ??= []).push(o)
    return out
  }, [data, stages])

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Relationships / Pipeline"
        title="Pipeline"
        description="Opportunities by stage. Every card carries a stage selector, so a stage change never requires a mouse."
        actions={<Link className="btn secondary" to="/relationships/opportunities"><span className="material-symbols-outlined">table_rows</span>Table</Link>}
      />
      <div className="body-scroll">
        {error && <ErrorState title="Could not load the pipeline" message={error.message} technical={error.technical} onRetry={reload} />}
        {loading ? <Spinner /> : (
          <div className="kanban">
            {Object.keys(byStage).map((stage) => (
              <div className="kb-col" key={stage}>
                <div className="kb-head">
                  <span className="kb-name">{stage}</span>
                  <span className="kb-count">{byStage[stage]?.length ?? 0}</span>
                </div>
                <div className="kb-cards">
                  {(byStage[stage] ?? []).map((o) => (
                    <article className="kb-card" key={o.OpportunityId}>
                      <div className="kb-card-top">
                        <div>
                          <div className="kb-title">{o.OpportunityName}</div>
                          <div className="kb-org">{o.OpportunityType ?? '—'}</div>
                        </div>
                      </div>
                      <div className="kb-move">{o.NextAction ?? 'No next action'}</div>
                      <div className="kb-foot">
                        {/* The keyboard route for changing stage — drag is the convenience,
                            not the mechanism (UI Constitution §10). */}
                        <select
                          className="ie-select"
                          style={{ minWidth: 0, fontSize: 'var(--fs-2xs)' }}
                          value={stage}
                          aria-label={`Move ${o.OpportunityName} to stage`}
                          onChange={(e) => void move(o, e.target.value)}
                        >
                          {(stages.includes(stage) ? stages : [stage, ...stages]).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        <span className="kb-value">{o.EstimatedValue ? `$${(Number(o.EstimatedValue) / 1000).toFixed(0)}K` : '—'}</span>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

// --------------------------------------------------------------------------------------------
// Activities
// --------------------------------------------------------------------------------------------

export function ActivitiesPage() {
  const { data, loading, error, reload } = useLoad(() => tfApi.fetchActivities({ limit: 200 }), [])
  return (
    <>
      <PageHeader
        crumb="Tidalfin / Engagement / Activities"
        title="Activities"
        description="Every interaction, newest first. Direction matters: only outbound moves the outreach clock the safe-outreach check reads."
      />
      <div className="body-scroll">
        {error && <ErrorState title="Could not load activities" message={error.message} technical={error.technical} onRetry={reload} />}
        {loading ? <Spinner /> : (data?.Items.length ?? 0) === 0 ? (
          <EmptyState icon="history" title="No activity yet" />
        ) : (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="dt">
                <caption className="sr-only">Activities with date, type, direction and outcome.</caption>
                <thead><tr><th>Date</th><th>Type</th><th>Direction</th><th>Subject</th><th>Outcome</th><th>Next Move</th></tr></thead>
                <tbody>
                  {data!.Items.map((a) => (
                    <tr key={a.ActivityId}>
                      <td><span className="person-sub">{formatShortDate(a.OccurredAt)}</span></td>
                      <td><b style={{ color: 'var(--tf-brand)', fontSize: 'var(--fs-sm)' }}>{a.ActivityType}</b></td>
                      <td>
                        <span className={`badge b-${a.Direction === 'INBOUND' ? 'strong' : 'neutral'}`}>
                          <span className="dot" />{a.Direction === 'INBOUND' ? 'Inbound' : a.Direction === 'INTERNAL' ? 'Internal' : 'Outbound'}
                        </span>
                      </td>
                      <td><span style={{ fontSize: 'var(--fs-sm)' }}>{a.Subject ?? a.Body?.slice(0, 60) ?? '—'}</span></td>
                      <td><span className="person-sub">{a.Outcome ?? '—'}</span></td>
                      <td><span className="next-move">{a.NextActionText ?? '—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-foot"><span>{data!.Items.length} activities</span></div>
          </div>
        )}
      </div>
    </>
  )
}

// --------------------------------------------------------------------------------------------
// Search
// --------------------------------------------------------------------------------------------

export function SearchPage() {
  const [params, setParams] = useSearchParams()
  const initial = params.get('q') ?? ''
  const [query, setQuery] = useState(initial)
  const [results, setResults] = useState<SearchResult[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [ran, setRan] = useState(false)

  const run = useCallback(async (q: string) => {
    if (!q.trim()) return
    setLoading(true)
    try {
      const r = await tfApi.search(q)
      setResults(r.Items)
      setTotal(r.Total)
      setRan(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (initial) void run(initial)
  }, [initial, run])

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Data / Search"
        title="Search"
        description="Search people, organizations, notes and activities. Every result explains why it matched."
      />
      <div className="body-scroll">
        <form
          className="ask-bar"
          style={{ marginBottom: 14 }}
          onSubmit={(e) => { e.preventDefault(); setParams({ q: query }); void run(query) }}
        >
          <span className="material-symbols-outlined" style={{ color: 'var(--tf-accent)' }}>manage_search</span>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Who do we know at insurance companies?" aria-label="Search" />
          <button className="btn primary sm" type="submit">Search</button>
        </form>

        {loading && <Spinner />}
        {!loading && ran && results.length === 0 && (
          <EmptyState icon="search_off" title="No matches" message={`Nothing matched “${query}”.`} />
        )}
        {!loading && results.length > 0 && (
          <>
            <div className="banner info">
              <span className="material-symbols-outlined">info</span>
              <div><b>{total} results.</b> Each explains why it matched — an unexplained ranking over your own relationships is not something you can act on.</div>
            </div>
            {results.map((r) => (
              <div className="card" style={{ marginBottom: 11 }} key={`${r.EntityType}-${r.EntityId}`}>
                <div className="card-body">
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 250 }}>
                      <div className="person-cell">
                        <Avatar name={r.DisplayName} />
                        <div>
                          {r.EntityType === 'PERSON' ? (
                            <Link className="person-name" to={`/relationships/people/${r.EntityId}`} style={{ fontSize: 'var(--fs-md)' }}>
                              {r.DisplayName}
                            </Link>
                          ) : (
                            <span className="person-name" style={{ fontSize: 'var(--fs-md)' }}>{r.DisplayName}</span>
                          )}
                          <div className="person-sub">{r.Subtitle ?? r.EntityType}</div>
                        </div>
                      </div>
                      {r.MatchExplanation && (
                        <div style={{ marginTop: 9, fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-relaxed)' }}>
                          <b style={{ color: 'var(--tf-accent-ink)' }}>Matched because: </b>
                          {r.MatchExplanation}
                        </div>
                      )}
                    </div>
                    {(r.NextMove || r.NextFollowUpDate) && (
                      <div style={{ width: 250 }}>
                        {/* The triad travels with the result so it can be acted on in place. */}
                        <div className="triad" style={{ gridTemplateColumns: 'minmax(0,1fr)', gap: 8, padding: '9px 11px' }}>
                          <div className="triad-item">
                            <span className="triad-label">Status</span>
                            <span className="triad-value" style={{ fontSize: 'var(--fs-sm)' }}>{r.ContactStatus ?? '—'}</span>
                          </div>
                          <div className="triad-item">
                            <span className="triad-label">Next Move</span>
                            <span className="triad-value move" style={{ fontSize: 'var(--fs-sm)' }}>{r.NextMove ?? '—'}</span>
                          </div>
                          <div className="triad-item">
                            <span className="triad-label">Follow-Up</span>
                            <span className="triad-value"><DueBadge date={r.NextFollowUpDate} /></span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  )
}

// --------------------------------------------------------------------------------------------
// Saved views, talent, reference administration
// --------------------------------------------------------------------------------------------

export function SavedViewsPage() {
  const { notify, notifyError } = useApp()
  const { data, loading, error, reload } = useLoad(() => tfApi.fetchSavedViews(), [])
  const [deleting, setDeleting] = useState<SavedView | null>(null)

  const remove = async (view: SavedView) => {
    try {
      await tfApi.deleteSavedView(view.SavedViewId)
      notify({ tone: 'ok', title: 'View deleted', body: view.ViewName })
      setDeleting(null)
      await reload()
    } catch (err) {
      notifyError('Could not delete the view', err)
      setDeleting(null)
    }
  }

  return (
    <>
      <PageHeader crumb="Tidalfin / Data / Saved Views" title="Saved Views" description="Filters you keep." />
      <div className="body-scroll">
        {error && <ErrorState title="Could not load saved views" message={error.message} technical={error.technical} onRetry={reload} />}
        {loading ? <Spinner /> : (data?.Items.length ?? 0) === 0 ? (
          <EmptyState icon="bookmark" title="No saved views" />
        ) : (
          <div className="table-wrap">
            <table className="dt">
              <caption className="sr-only">Saved views with entity type, definition and pin state.</caption>
              <thead><tr><th>View</th><th>Applies To</th><th>Definition</th><th>Pinned</th><th /></tr></thead>
              <tbody>
                {data!.Items.map((v) => (
                  <tr key={v.SavedViewId}>
                    <td><span className="person-name">{v.ViewName}</span></td>
                    <td><Tag>{v.EntityType}</Tag></td>
                    <td><span className="person-sub" style={{ fontStyle: 'italic' }}>{JSON.stringify(v.FilterDefinition ?? {})}</span></td>
                    <td>{v.IsPinned ? <span className="badge b-strong"><span className="dot" />Pinned</span> : <span className="person-sub">—</span>}</td>
                    <td><button className="btn ghost sm" type="button" onClick={() => setDeleting(v)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="table-foot"><span>{data!.Items.length} views</span></div>
          </div>
        )}
      </div>
      {deleting && (
        <ConfirmDialog
          title="Delete this saved view?"
          message={`“${deleting.ViewName}” will be removed. The relationships it selects are not affected.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => void remove(deleting)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

//  CandidatesPage MOVED to `talent_candidates.tsx`. The version that lived here listed
//  TfTalentProfiles, and nothing created one — so the screen was empty with ten people in the
//  system and nothing saying why. Candidate status now lives on the relationship, so every person
//  can be a candidate without another record having to exist first.

export function ReferenceAdminPage() {
  const { reference } = useApp()
  const domains = Object.keys(reference).sort()
  const [selected, setSelected] = useState(domains[0] ?? '')
  const values = reference[selected] ?? []

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Administration / Categories & Statuses"
        title="Categories & Statuses"
        description={`${domains.length} reference domains. Every status, type and category is configuration served by the service — the client renders whatever comes back.`}
      />
      <section className="page-toolbar">
        <div className="chip-row">
          {domains.map((d) => (
            <button key={d} type="button" className={`chip${selected === d ? ' on' : ''}`} onClick={() => setSelected(d)}>
              {d.replaceAll('_', ' ').toLowerCase()} <span className="cn">{reference[d].length}</span>
            </button>
          ))}
        </div>
      </section>
      <div className="body-scroll">
        <div className="banner info">
          <span className="material-symbols-outlined">palette</span>
          <div>
            <b>The Indicator column holds a token name, never a colour.</b> A value says what it
            <em> means</em> — healthy, waiting, still, stop — and the stylesheet decides what that
            looks like. A hex here would mean a rebrand required a data migration.
          </div>
        </div>
        {values.length === 0 ? (
          <EmptyState icon="category" title="No values" />
        ) : (
          <div className="table-wrap">
            <table className="dt">
              <caption className="sr-only">Reference values with code, indicator token, order and system flag.</caption>
              <thead><tr><th>Value</th><th>Code</th><th>Indicator token</th><th>Order</th><th>System</th></tr></thead>
              <tbody>
                {values.map((v) => (
                  <tr key={v.ReferenceValueId}>
                    <td><b style={{ color: 'var(--tf-brand)' }}>{v.ValueLabel}</b></td>
                    <td><code style={{ fontFamily: 'var(--tf-font-mono)', fontSize: 'var(--fs-xs)' }}>{v.ValueCode}</code></td>
                    <td>{v.IndicatorColor ? <span className={`badge b-${v.IndicatorColor}`}><span className="dot" />{v.IndicatorColor}</span> : <span className="person-sub">—</span>}</td>
                    <td className="num"><span className="person-sub">{v.SortOrder}</span></td>
                    <td>{v.IsSystem ? <Tag>System</Tag> : <span className="person-sub">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="table-foot"><span>{values.length} values in {selected}</span></div>
          </div>
        )}
      </div>
    </>
  )
}

export function TagsPage() {
  const { notify, notifyError } = useApp()
  const { data, loading, error, reload } = useLoad(() => tfApi.fetchTags(), [])
  const [name, setName] = useState('')

  const create = async () => {
    if (!name.trim()) return
    try {
      await tfApi.createTag(name.trim())
      notify({ tone: 'ok', title: 'Tag created', body: name })
      setName('')
      await reload()
    } catch (err) {
      notifyError('Could not create the tag', err)
    }
  }

  return (
    <>
      <PageHeader crumb="Tidalfin / Administration / Tags" title="Tags" description="Free labels that complement the structured fields." />
      <div className="body-scroll">
        {error && <ErrorState title="Could not load tags" message={error.message} technical={error.technical} onRetry={reload} />}
        <div className="quick-log" style={{ marginBottom: 14, maxWidth: 460 }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New tag name" aria-label="New tag name"
                 onKeyDown={(e) => e.key === 'Enter' && void create()} />
          <button className="btn primary sm" type="button" onClick={() => void create()}>Add</button>
        </div>
        {loading ? <Spinner /> : (data?.Items.length ?? 0) === 0 ? (
          <EmptyState icon="sell" title="No tags yet" />
        ) : (
          <div className="table-wrap">
            <table className="dt">
              <caption className="sr-only">Tags with usage count.</caption>
              <thead><tr><th>Tag</th><th>Used</th></tr></thead>
              <tbody>
                {data!.Items.map((t) => (
                  <tr key={t.TagId}>
                    <td><Tag>{t.TagName}</Tag></td>
                    <td className="num"><b style={{ color: 'var(--tf-brand)' }}>{t.UsageCount}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="table-foot"><span>{data!.Items.length} tags</span></div>
          </div>
        )}
      </div>
    </>
  )
}

export function SettingsPage() {
  const { config, userId } = useApp()
  return (
    <>
      <PageHeader crumb="Tidalfin / Administration / Settings" title="Settings" description="Account, environment and configured behaviour." />
      <div className="body-scroll">
        <div className="banner info">
          <span className="material-symbols-outlined">business</span>
          <div>
            <b>Account data comes from the existing Account Service on port 8011.</b> Tidalfin reads
            it and does not manage accounts, so standing Tidalfin up needs no change to that service.
          </div>
        </div>
        <div className="two-col">
          <div className="card">
            <div className="card-head"><span className="card-title"><span className="material-symbols-outlined">business</span>Client Account</span></div>
            <div className="card-body">
              <dl className="kv">
                <dt>Account</dt><dd>{config?.tenantName}</dd>
                <dt>Account Id</dt><dd><code style={{ fontFamily: 'var(--tf-font-mono)', fontSize: 'var(--fs-xs)' }}>{config?.clientAccountId}</code></dd>
                <dt>Environment</dt><dd>{config?.environment}</dd>
                <dt>Signed in as</dt><dd>{userId}</dd>
              </dl>
            </div>
          </div>
          <div className="card">
            <div className="card-head"><span className="card-title"><span className="material-symbols-outlined">shield</span>Safe Outreach</span></div>
            <div className="card-body">
              <dl className="kv">
                <dt>Recent outreach</dt><dd>{config?.safeOutreach?.recent_outreach_days} days</dd>
                <dt>Dormant after</dt><dd>{config?.safeOutreach?.dormant_relationship_days} days</dd>
                <dt>Blocking risks</dt><dd>{(config?.safeOutreach?.blocking_risks ?? []).join(', ')}</dd>
              </dl>
              <p className="person-sub" style={{ marginTop: 10 }}>
                Blocking severities are not configurable. A safeguard that can be switched off under
                deadline pressure is a safeguard that will be switched off.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// --------------------------------------------------------------------------------------------
// Module overviews
// --------------------------------------------------------------------------------------------

export function ModuleOverview({
  crumb, title, description, cards,
}: {
  crumb: string
  title: string
  description: string
  cards: Array<{ name: string; icon: string; to: string; desc: string }>
}) {
  return (
    <>
      <PageHeader crumb={crumb} title={title} description={description} />
      <div className="body-scroll">
        <div className="three-col">
          {cards.map((c) => (
            <Link className="card" to={c.to} key={c.to} style={{ textDecoration: 'none' }}>
              <div className="card-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                  <span className="tl-icon" style={{ width: 34, height: 34 }}>
                    <span className="material-symbols-outlined">{c.icon}</span>
                  </span>
                  <span className="card-title">{c.name}</span>
                </div>
                <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--tf-muted)', lineHeight: 'var(--lh-relaxed)' }}>
                  {c.desc}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </>
  )
}

export function NotBuiltYet({ title, crumb, phase, description }: {
  title: string; crumb: string; phase: string; description: string
}) {
  return (
    <>
      <PageHeader crumb={crumb} title={title} />
      <div className="body-scroll">
        {/* Honest about what does not exist yet. A screen that pretends to work is worse than one
            that says what it is waiting for. */}
        <div className="banner warn">
          <span className="material-symbols-outlined">construction</span>
          <div>
            <b>{phase}.</b> {description}
          </div>
        </div>
      </div>
    </>
  )
}
